import React, { useState, useEffect, useRef } from 'react';
import { 
  Box, 
  TextField, 
  Button, 
  Grid, 
  Typography, 
  Paper,
  MenuItem,
  InputAdornment
} from '@mui/material';
import { useAuth, useNotification, ClientRegistrationData } from 'shared-lib';
import { useClientQueries } from 'shared-lib';

// List of US states for dropdown
const US_STATES = [
  'Alabama', 'Alaska', 'Arizona', 'Arkansas', 'California', 'Colorado', 'Connecticut', 
  'Delaware', 'Florida', 'Georgia', 'Hawaii', 'Idaho', 'Illinois', 'Indiana', 'Iowa', 
  'Kansas', 'Kentucky', 'Louisiana', 'Maine', 'Maryland', 'Massachusetts', 'Michigan', 
  'Minnesota', 'Mississippi', 'Missouri', 'Montana', 'Nebraska', 'Nevada', 'New Hampshire', 
  'New Jersey', 'New Mexico', 'New York', 'North Carolina', 'North Dakota', 'Ohio', 
  'Oklahoma', 'Oregon', 'Pennsylvania', 'Rhode Island', 'South Carolina', 'South Dakota', 
  'Tennessee', 'Texas', 'Utah', 'Vermont', 'Virginia', 'Washington', 'West Virginia', 
  'Wisconsin', 'Wyoming'
];

interface ClientRegistrationFormProps {
  onSuccess?: () => void;
  onError?: (error: Error) => void;
}

// Интерфейс для ошибок
interface FormErrors {
  companyName?: string;
  city?: string;
  state?: string;
  address?: string;
  zipCode?: string;
  contactFullName?: string;
  contactEmail?: string;
  contactPhoneNumber?: string;
  commercialGeneralLiabilityAmount?: string;
  workersCompensationAmount?: string;
  automobileLiabilityAmount?: string;
  umbrellaAmount?: string;
}

// Регулярные выражения для валидации
const EMAIL_REGEX = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;
const PHONE_REGEX = /^\(?\d{3}\)?[-.\s]?\d{3}[-.\s]?\d{4}$/; // Формат: (123) 456-7890 или 123-456-7890
const ZIP_REGEX = /^\d{5}(-\d{4})?$/; // Формат: 12345 или 12345-6789

export const ClientRegistrationForm: React.FC<ClientRegistrationFormProps> = ({
  onSuccess,
  onError
}) => {
  const [formData, setFormData] = useState<ClientRegistrationData>({
    companyName: '',
    city: '',
    state: '',
    address: '',
    zipCode: '',
    contactFullName: '',
    contactEmail: '',
    contactPhoneNumber: '',
    commercialGeneralLiabilityAmount: undefined,
    workersCompensationAmount: undefined,
    automobileLiabilityAmount: undefined,
    umbrellaAmount: undefined
  });
  
  const [errors, setErrors] = useState<FormErrors>({});
  const [error, setError] = useState<string | null>(null);
  const [touched, setTouched] = useState<Record<string, boolean>>({});
  const { currentUser } = useAuth();
  const { showNotification } = useNotification();
  
  // Use the useClientQueries hook to access the client creation functionality
  const clientQueries = useClientQueries();
  const { useCreateClient } = clientQueries;
  const createClient = useCreateClient();

  // Валидация полей при изменении данных
  useEffect(() => {
    validateField();
  }, [formData]);

  const validateField = (field?: string) => {
    const newErrors: FormErrors = { ...errors };
    
    // Если передано конкретное поле, проверяем только его
    if (field) {
      validateSingleField(field, newErrors);
    } else {
      // Проверяем все поля
      Object.keys(formData).forEach(fieldName => {
        validateSingleField(fieldName, newErrors);
      });
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };
  
  const validateSingleField = (fieldName: string, errorsObj: FormErrors) => {
    const value = (formData as any)[fieldName];
    
    // Очищаем предыдущую ошибку для поля
    delete errorsObj[fieldName as keyof FormErrors];
    
    if (!value || value.trim() === '') {
      // Only make required fields show error
      if (!['commercialGeneralLiabilityAmount', 'workersCompensationAmount', 
            'automobileLiabilityAmount', 'umbrellaAmount'].includes(fieldName)) {
        errorsObj[fieldName as keyof FormErrors] = 'This field is required';
      }
    } else {
      // Дополнительная валидация для конкретных полей
      switch (fieldName) {
        case 'contactEmail':
          if (!EMAIL_REGEX.test(value)) {
            errorsObj.contactEmail = 'Please enter a valid email address';
          }
          break;
        case 'contactPhoneNumber':
          if (!PHONE_REGEX.test(value)) {
            errorsObj.contactPhoneNumber = 'Please enter a valid phone number (e.g., 123-456-7890)';
          }
          break;
        case 'zipCode':
          if (!ZIP_REGEX.test(value)) {
            errorsObj.zipCode = 'Please enter a valid ZIP code (e.g., 12345 or 12345-6789)';
          }
          break;
        case 'commercialGeneralLiabilityAmount':
        case 'workersCompensationAmount':
        case 'automobileLiabilityAmount':
        case 'umbrellaAmount':
          if (value && isNaN(Number(value))) {
            errorsObj[fieldName as keyof FormErrors] = 'Please enter a valid whole number';
          }
          break;
      }
    }
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleBlur = (e: React.FocusEvent<HTMLInputElement>) => {
    const { name } = e.target;
    setTouched(prev => ({
      ...prev,
      [name]: true
    }));
    validateField(name);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!currentUser) {
      setError('You must be logged in to register a client');
      return;
    }
    
    // Валидируем все поля перед отправкой и помечаем их как тронутые
    const touchedFields: Record<string, boolean> = {};
    Object.keys(formData).forEach(key => {
      touchedFields[key] = true;
    });
    setTouched(touchedFields);
    
    if (!validateField()) {
      setError('Please correct all errors before submitting.');
      return;
    }
    
    setError(null);
    
    try {
      // Use React Query mutation instead of FirestoreService
      await createClient.mutateAsync(formData);
      
      if (onSuccess) {
        onSuccess();
      }
    } catch (err) {
      const error = err as Error;
      setError(error.message);
      if (onError) {
        onError(error);
      }
    }
  };

  // Use React Query's isPending state instead of local loading state
  const loading = createClient.isPending;

  // Проверка, есть ли ошибка и было ли поле тронутым
  const hasError = (fieldName: keyof FormErrors) => 
    touched[fieldName] && Boolean(errors[fieldName]);
  
  // Получение текста ошибки
  const getErrorText = (fieldName: keyof FormErrors) => 
    touched[fieldName] ? errors[fieldName] : '';

  // Show error notification when there's an error
  const prevErrorRef = useRef<string | null>(null);
  useEffect(() => {
    if (error && error !== prevErrorRef.current) {
      prevErrorRef.current = error;
      showNotification({
        message: error,
        severity: 'error'
      });
    }
  }, [error, showNotification]);
  
  return (
    <Paper elevation={3} sx={{ p: 3, borderRadius: 2 }}>
      <Box component="form" onSubmit={handleSubmit} noValidate>
        <Grid container spacing={2}>
          <Grid item xs={12}>
            <TextField
              required
              fullWidth
              id="companyName"
              name="companyName"
              label="Property Name"
              value={formData.companyName}
              onChange={handleChange}
              onBlur={handleBlur}
              disabled={loading}
              error={hasError('companyName')}
              helperText={getErrorText('companyName')}
            />
          </Grid>
          
          <Grid item xs={12}>
            <TextField
              required
              fullWidth
              id="address"
              name="address"
              label="Address"
              value={formData.address}
              onChange={handleChange}
              onBlur={handleBlur}
              disabled={loading}
              error={hasError('address')}
              helperText={getErrorText('address')}
            />
          </Grid>
          
          <Grid item xs={12} sm={6}>
            <TextField
              required
              fullWidth
              id="city"
              name="city"
              label="City"
              value={formData.city}
              onChange={handleChange}
              onBlur={handleBlur}
              disabled={loading}
              error={hasError('city')}
              helperText={getErrorText('city')}
            />
          </Grid>
          
          <Grid item xs={12} sm={3}>
            <TextField
              required
              fullWidth
              select
              id="state"
              name="state"
              label="State"
              value={formData.state}
              onChange={handleChange}
              onBlur={handleBlur}
              disabled={loading}
              error={hasError('state')}
              helperText={getErrorText('state')}
            >
              {US_STATES.map(state => (
                <MenuItem key={state} value={state}>
                  {state}
                </MenuItem>
              ))}
            </TextField>
          </Grid>
          
          <Grid item xs={12} sm={3}>
            <TextField
              required
              fullWidth
              id="zipCode"
              name="zipCode"
              label="Zip Code"
              value={formData.zipCode}
              onChange={handleChange}
              onBlur={handleBlur}
              disabled={loading}
              error={hasError('zipCode')}
              helperText={getErrorText('zipCode')}
            />
          </Grid>
          
          <Grid item xs={12}>
            <TextField
              required
              fullWidth
              id="contactFullName"
              name="contactFullName"
              label="Contact Full Name"
              value={formData.contactFullName}
              onChange={handleChange}
              onBlur={handleBlur}
              disabled={loading}
              error={hasError('contactFullName')}
              helperText={getErrorText('contactFullName')}
            />
          </Grid>
          
          <Grid item xs={12} sm={6}>
            <TextField
              required
              fullWidth
              id="contactEmail"
              name="contactEmail"
              label="Contact Email"
              type="email"
              value={formData.contactEmail}
              onChange={handleChange}
              onBlur={handleBlur}
              disabled={loading}
              error={hasError('contactEmail')}
              helperText={getErrorText('contactEmail')}
            />
          </Grid>
          
          <Grid item xs={12} sm={6}>
            <TextField
              required
              fullWidth
              id="contactPhoneNumber"
              name="contactPhoneNumber"
              label="Contact Phone Number"
              value={formData.contactPhoneNumber}
              onChange={handleChange}
              onBlur={handleBlur}
              disabled={loading}
              error={hasError('contactPhoneNumber')}
              helperText={getErrorText('contactPhoneNumber')}
            />
          </Grid>

          <Grid item xs={12}>
            <Typography variant="h6" sx={{ mt: 2, mb: 1 }}>
              Insurance Requirements for Working on Site
            </Typography>
          </Grid>
          
          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              id="commercialGeneralLiabilityAmount"
              name="commercialGeneralLiabilityAmount"
              label="Commercial General Liability Amount"
              type="number"
              value={formData.commercialGeneralLiabilityAmount || ''}
              onChange={handleChange}
              onBlur={handleBlur}
              disabled={loading}
              error={hasError('commercialGeneralLiabilityAmount')}
              helperText={getErrorText('commercialGeneralLiabilityAmount')}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">$</InputAdornment>
                ),
                inputProps: { min: 0, step: 1 }
              }}
            />
          </Grid>
          
          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              id="workersCompensationAmount"
              name="workersCompensationAmount"
              label="Workers Compensation Amount"
              type="number"
              value={formData.workersCompensationAmount || ''}
              onChange={handleChange}
              onBlur={handleBlur}
              disabled={loading}
              error={hasError('workersCompensationAmount')}
              helperText={getErrorText('workersCompensationAmount')}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">$</InputAdornment>
                ),
                inputProps: { min: 0, step: 1 }
              }}
            />
          </Grid>
          
          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              id="automobileLiabilityAmount"
              name="automobileLiabilityAmount"
              label="Automobile Liability Amount"
              type="number"
              value={formData.automobileLiabilityAmount || ''}
              onChange={handleChange}
              onBlur={handleBlur}
              disabled={loading}
              error={hasError('automobileLiabilityAmount')}
              helperText={getErrorText('automobileLiabilityAmount')}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">$</InputAdornment>
                ),
                inputProps: { min: 0, step: 1 }
              }}
            />
          </Grid>
          
          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              id="umbrellaAmount"
              name="umbrellaAmount"
              label="Umbrella Amount"
              type="number"
              value={formData.umbrellaAmount || ''}
              onChange={handleChange}
              onBlur={handleBlur}
              disabled={loading}
              error={hasError('umbrellaAmount')}
              helperText={getErrorText('umbrellaAmount')}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">$</InputAdornment>
                ),
                inputProps: { min: 0, step: 1 }
              }}
            />
          </Grid>
        </Grid>
        
        <Box sx={{ mt: 3, display: 'flex', justifyContent: 'center' }}>
          <Button
            type="submit"
            variant="contained"
            color="primary"
            size="large"
            disabled={loading}
            sx={{ minWidth: 200 }}
          >
            {loading ? 'Registering...' : 'Register Property'}
          </Button>
        </Box>
      </Box>
    </Paper>
  );
}; 